/**
 *   Created by Arif Topcu in 2018
 */
import React, {useCallback, useEffect} from 'react';
import Combobox from '@ls/react-combobox-grid';
import {useDispatch, useSelector} from 'react-redux';
import SelectionState from 'state/selections/SelectionState';
import {setSelectedBenchmarkEntityId} from 'state/selections/actions';
import {selectSelectedAccountEntityId, selectSelectedBenchmarkEntityId, selectSelectedIsDefaultBenchmark} from 'state/selections/selectors';
import {selectFetchingEntities} from 'state/data/selectors';
import {useEntitySelectorOptions, useGetDefaultBenchmarkEntity} from 'state/selections';
import Tooltip from '@ls/react-tooltip';
import styled from 'styled-components';

const columnDefs = [
  {
    headerName: 'Entity ID',
    field: 'entityId',
    width: 85,
    suppressSizeToFit: true,
  },
  {
    headerName: 'Account/Benchmark',
    field: 'label',
  },
];

const Text = styled.span`
  font-size: 1.2em;
`;

const WarningTooltip = <Text>Turn off <b>Use Default Benchmark</b> to select a custom benchmark.</Text>;

const BenchmarkSelector = props => {
  const dispatch = useDispatch();
  const fetchingEntities = useSelector(selectFetchingEntities);
  const isDefaultBenchmark = useSelector(selectSelectedIsDefaultBenchmark(SelectionState.UNCOMMITTED));
  const entityId = useSelector(selectSelectedBenchmarkEntityId(SelectionState.UNCOMMITTED));
  const onChange = useCallback((entityId) => dispatch(setSelectedBenchmarkEntityId(entityId)), [dispatch]);
  const getDefaultBenchmarkEntity = useGetDefaultBenchmarkEntity();
  const accEntityId = useSelector(
    selectSelectedAccountEntityId(SelectionState.UNCOMMITTED)
  );

  useEffect(() => {
    if (isDefaultBenchmark) {
      const defaultBenchmarkEntity = getDefaultBenchmarkEntity(accEntityId);
      if (defaultBenchmarkEntity) {
        dispatch(
          setSelectedBenchmarkEntityId(defaultBenchmarkEntity.entityId)
        );
      } else {
        dispatch(setSelectedBenchmarkEntityId(null));
      }
    }
  }, [dispatch, getDefaultBenchmarkEntity, accEntityId, isDefaultBenchmark]);
  
  const entitySelectorOptions = useEntitySelectorOptions();
  const tooltip = isDefaultBenchmark ? WarningTooltip : undefined;
  return (
    <Tooltip title={tooltip}>
      <div>
        <Combobox
          options={entitySelectorOptions}
          onChange={onChange}
          value={entityId}
          loading={fetchingEntities}
          disabled={isDefaultBenchmark}
          placeholder="Accounts, Benchmarks..."
          label="Benchmark Entity:"
          valueKey="entityId"
          labelKey="label"
          columnDefs={columnDefs}
          variation={1}
          enableSpeedDial
          persistKey="COMBOBOX_BMK"
        />
      </div>
    </Tooltip>
  );
};

export default BenchmarkSelector;
